import { string } from "yup";
import type { DeliveryStepFormErrorHeaders } from "../deliveryStepValidationSchema";
import { notOnlyWhitespace } from "@lib/validators/regex/general";
import { postalCodeRegex } from "@lib/validators/regex/postalCodeRegex";
import { getStates } from "@components/commerce/baseForms/helpers/helpers";
import { Market, type CountryCode } from "@config/site/types";
import { ShippingMethodTypes } from "@features/checkout/types";

const getMarketPostalCode = (market: Market, headers: DeliveryStepFormErrorHeaders) => {
    const postalCodeValidation = postalCodeRegex[market];
    if (!postalCodeValidation) {
        return undefined;
    }

    return string()
        .label(headers.postalCodeLabel)
        .matches(postalCodeValidation, headers.zipCodeValid)
        .required();
};

export const ADDRESS_LINE_1_MAX_LENGTH = 35;

// Applied only by the market schemas that opt in; their carriers reject address
// line 1 over this length, so the error asks the customer to use line 2.
const getAddressLine1Schema = (headers: DeliveryStepFormErrorHeaders) =>
    string()
        .label(headers.streetNameLabel)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(ADDRESS_LINE_1_MAX_LENGTH, headers.streetNameMaxLength)
        .required();

export const getSchemaMY = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.MY, headers),
});

export const getSchemaIN = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.IN, headers),
    additionalAddressInfo: string()
        .label(headers.addressLine2Label)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(40, headers.maxAddressCharacters)
        .required(),
    landmark: string().max(40, headers.maxAddressCharacters),
    deliveryInstructions: string().max(100, headers.maxAddressCharacters),
});

export const getSchemaSG = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.SG, headers),
});

export const getSchemaES = (headers: DeliveryStepFormErrorHeaders, forProfile?: boolean) => ({
    postalCode: getMarketPostalCode(Market.ES, headers),
    ...(!forProfile && { streetName: getAddressLine1Schema(headers) }),
});

export const getSchemaCABillingAddress = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.CA, headers),
});

export const getSchemaCA = (headers: DeliveryStepFormErrorHeaders) => ({
    streetName: string()
        .label(headers.streetNameLabel)
        .max(40, headers.maxAddressCharacters)
        .required(),
    additionalAddressInfo: string().max(40, headers.maxAddressCharacters),
    ...getSchemaCABillingAddress(headers),
});

export const getSchemaUS = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.US, headers),
});

export const getSchemaAU = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.AU, headers),
});

/** NZ postcode format only (profile + checkout). */
export const getSchemaNZPostalCode = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.NZ, headers),
});

/** Checkout: NZ postcode + required district (suburb). */
export const getSchemaNZ = (
    headers: DeliveryStepFormErrorHeaders,
    isDistrictFieldEnabled = true
) => ({
    ...getSchemaNZPostalCode(headers),
    // District is only required when its field is shown for the locale.
    ...(isDistrictFieldEnabled
        ? { district: string().label(headers.districtLabel).max(35).required() }
        : {}),
});

// Identical functions are valid for these because we would rather have predictable schemas than DRY code
export const getSchemaEE = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.EE, headers),
});

export const getSchemaKR = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.KR, headers),
    additionalAddressInfo: string()
        .label(headers.addressLine2Label)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(40, headers.maxAddressCharacters)
        .required(),
});

export const getSchemaJP = (headers: DeliveryStepFormErrorHeaders) => ({
    phoneticFirstName: string()
        .label(headers.phoneticFirstNameLabel)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(25, headers.maxNameCharacters)
        .required(),
    phoneticLastName: string()
        .label(headers.phoneticLastNameLabel)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(25, headers.maxNameCharacters)
        .required(),
    postalCode: getMarketPostalCode(Market.JP, headers),
    additionalAddressInfo: string()
        .label(headers.addressLine2Label)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(40, headers.maxAddressCharacters)
        .required(),
});

export const getSchemaGB = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: string()
        .label(headers.postalCodeLabel)
        .max(8, headers.zipCodeValid)
        .test({
            name: "postalCodeGB",
            message: headers.zipCodeValid,
            test: function (value) {
                if (!value) return true;

                // Remove starting and ending spaces
                const trimmed = value.trim();

                // Check if there's exactly one space in the middle
                const spaceIndex = trimmed.indexOf(" ");
                if (spaceIndex === -1) return false;

                // Split into outward and inward codes
                const outwardCode = trimmed.substring(0, spaceIndex);
                const inwardCode = trimmed.substring(spaceIndex + 1);

                // Check inward code: exactly 3 characters, pattern 1 digit + 2 letters
                if (inwardCode.length !== 3) return false;
                const inwardCodePattern = /^[0-9][A-Za-z]{2}$/;
                if (!inwardCodePattern.test(inwardCode)) return false;

                // Check outward code patterns: AN, ANN, AAN, AANN, ANA, AANA only
                if (outwardCode.length === 2) {
                    // AN pattern: 1 letter + 1 digit
                    return /^[A-Za-z]\d$/.test(outwardCode);
                } else if (outwardCode.length === 3) {
                    // ANN pattern: 1 letter + 2 digits OR AAN pattern: 2 letters + 1 digit OR ANA pattern: 1 letter + 1 digit + 1 letter
                    return (
                        /^[A-Za-z]\d{2}$/.test(outwardCode) ||
                        /^[A-Za-z]{2}\d$/.test(outwardCode) ||
                        /^[A-Za-z]\d[A-Za-z]$/.test(outwardCode)
                    );
                } else if (outwardCode.length === 4) {
                    // AANN pattern: 2 letters + 2 digits OR AANA pattern: 2 letters + 1 digit + 1 letter
                    return (
                        /^[A-Za-z]{2}\d{2}$/.test(outwardCode) ||
                        /^[A-Za-z]{2}\d[A-Za-z]$/.test(outwardCode)
                    );
                }

                return false;
            },
        })
        .required(),
});

export const getSchemaPL = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.PL, headers),
});

export const getSchemaPT = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.PT, headers),
});

export const getSchemaSK = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.SK, headers),
});

export const getSchemaSI = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.SI, headers),
});

export const getSchemaHU = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.HU, headers),
});

export const getSchemaHR = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.HR, headers),
});

export const getSchemaFR = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.FR, headers),
});

export const getSchemaBE = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.BE, headers),
});

export const getSchemaCZ = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.CZ, headers),
});

export const getSchemaAT = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.AT, headers),
});

export const getSchemaCH = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.CH, headers),
});

export const getSchemaDE = (
    headers: DeliveryStepFormErrorHeaders,
    addressForm?: "deliveryAddress" | "billingAddress",
    forProfile?: boolean
) => ({
    postalCode: getMarketPostalCode(Market.DE, headers),
    ...(!forProfile && { streetName: getAddressLine1Schema(headers) }),
    customerNumber: string()
        .label(headers.customerNumberLabel)
        .test({
            name: "customerNumber",
            message: ({ value }) => {
                if (!value) return headers.requiredField;
                return headers.customerNumber;
            },
            test: function (value, context) {
                const formValues = context?.options?.context;
                if (formValues?.deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) {
                    if (!value) {
                        return false;
                    }
                    const customerNumberRegex = /^\d{6,10}$/;
                    return customerNumberRegex.test(value);
                }
                return true;
            },
        }),
    ...(addressForm === "billingAddress" && {
        salutation: string()
            .label(headers.salutationLabel)
            .test({
                name: "salutation",
                message: headers.requiredField,
                test: function (value, context) {
                    const formValues = context?.options?.context;
                    if (formValues?.deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) {
                        if (!value) {
                            return false;
                        }
                        return true;
                    }
                    return true;
                },
            }),
    }),
});

export const getSchemaNL = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.NL, headers),
});

export const getSchemaLV = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.LV, headers),
});

export const getSchemaLT = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.LT, headers),
});

export const getSchemaSE = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.SE, headers),
});

export const getSchemaDK = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.DK, headers),
});

export const getSchemaFI = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.FI, headers),
});

export const getSchemaIE = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: string()
        .label(headers.postalCodeLabel)
        .matches(notOnlyWhitespace, headers.requiredField)
        .max(8, headers.zipCodeValid)
        .required(),
});

export const getSchemaNO = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.NO, headers),
});

export const getSchemaCY = (headers: DeliveryStepFormErrorHeaders) => ({
    postalCode: getMarketPostalCode(Market.CY, headers),
});

export function getRegionSchema(
    headers: DeliveryStepFormErrorHeaders,
    country: CountryCode,
    locale: string,
    isStateFieldEnabled = true
) {
    return {
        // Region is only required when its field is shown for the locale.
        region:
            isStateFieldEnabled && getStates(country, locale).length
                ? string()
                      .label(headers.regionLabel)
                      .oneOf(
                          getStates(country, locale)?.map(
                              (state: { value: string }) => state.value
                          ) || []
                      )
                      .required()
                : string().nullable(),
    };
}
